import React from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { humanEUR } from "../../cost-control/forecast/format";
import IdleCapacityNote from "./IdleCapacityNote";

const CHART_H = 220;
const ACTUAL = "hsl(var(--chart-1))";
const SHADOW = "hsl(var(--chart-teal))"; // the same teal Cost & Usage uses for API-equivalent

// EconomicsTooltip mirrors ForecastTooltip's card shape (dark-mode-safe theme tokens,
// not recharts' default contentStyle which is hardcoded light).
function EconomicsTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
      <div className="mb-1.5 text-sm font-semibold text-text-2">{label}</div>
      {row.actual != null && (
        <div className="flex items-center justify-between gap-4 text-xs text-text-2">
          <span>Actual cost</span>
          <span className="tabular-nums text-text">{humanEUR(row.actual)}</span>
        </div>
      )}
      {row.apiEquivalent != null && (
        <div className="flex items-center justify-between gap-4 text-xs text-text-2">
          <span>API-equivalent value</span>
          <span className="tabular-nums text-text">{humanEUR(row.apiEquivalent)}</span>
        </div>
      )}
    </div>
  );
}

// AI consumption economics — actual incurred cost against API-equivalent
// consumption value, over time.
//
// This exists because subscription AI usage behaves unlike normal cloud
// consumption: €10,000 of equivalent API consumption can produce €300 of
// actual subscription expense. Reporting only the €300 invites the reading
// that AI is economically insignificant. The two numbers are deliberately
// different concepts and the section states both.
// idleCapacity: the Actual cost LINE here is the sum of the breakdown rows, so
// it excludes idle capacity while the Actual KPI above includes it. Same words
// as the contributors footnote — see IdleCapacityNote.
export default function EconomicsChart({ economics, idleCapacity }) {
  const data = economics || [];

  return (
    <div className="rounded-card border border-card-bd bg-card p-4">
      <div className="text-[15px] font-semibold text-text">AI consumption economics</div>
      <div className="mt-0.5 text-[12px] text-text-3">
        Accounting expenditure against economic consumption value. These are different concepts,
        not two estimates of the same one.
      </div>

      <IdleCapacityNote idleCapacity={idleCapacity} className="mt-1" />

      {data.length === 0 ? (
        <div className="py-12 text-center text-[13px] text-text-3">No AI cost in this period.</div>
      ) : (
        <div style={{ height: CHART_H }} className="mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="hsl(var(--chart-axis))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--chart-axis))" tickFormatter={humanEUR} />
              <Tooltip content={<EconomicsTooltip />} cursor={{ stroke: "hsl(var(--chart-axis))", strokeWidth: 1 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="apiEquivalent"
                name="API-equivalent value"
                stroke={SHADOW}
                fill={SHADOW}
                fillOpacity={0.12}
              />
              <Line type="monotone" dataKey="actual" name="Actual cost" stroke={ACTUAL} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
